import type { ReactNode } from 'react'
import { AnimatePresence, m } from 'framer-motion'

/* The pieces of a working day that scramble and sort on the sign-in stage.
   Each has a sorted slot in the grid (`order`) and one pose per scramble
   (`chaos`, alternating each round). Coordinates are the card's top-left on
   the 720×900 stage. Cards use the app's own surface tokens, so they follow
   light and dark mode like the product does. */

export const STAGE_W = 720
export const STAGE_H = 900

interface Pose {
  x: number
  y: number
  r: number
  s?: number
}

export interface FragmentState {
  /** How many of the open tasks have been ticked off this round (0–3). */
  checked: number
  isPaid: boolean
}

export interface Fragment {
  id: string
  w: number
  h: number
  /** Stacking while scrambled (sorted cards all sit flat). */
  z: number
  order: { x: number; y: number; r?: number }
  chaos: [Pose, Pose]
  render: (state: FragmentState) => ReactNode
}

const EASE = [0.22, 1, 0.36, 1] as const
const L = 60
const R = 368
const COL = 292

/* Light mode gets real card stock — warm off-white with a lit top edge and
   a warm hairline — instead of flat white on a gray border; dark mode keeps
   the app's own card surface. Two cards change material for rhythm: the
   meeting carries a calendar-blue wash, the timer is cast iron. */
const TONES = {
  paper:
    'border-[hsl(32_22%_83%)] bg-[linear-gradient(180deg,hsl(40_45%_99.8%),hsl(38_32%_97.6%))] text-[hsl(30_14%_12%)] shadow-[inset_0_1px_0_hsl(0_0%_100%)]',
  blue: 'border-[hsl(220_32%_84%)] bg-[linear-gradient(180deg,hsl(220_80%_97%),hsl(40_45%_99.6%)_72%)] text-[hsl(30_14%_12%)] shadow-[inset_0_1px_0_hsl(0_0%_100%)]',
  iron: 'border-[hsl(30_8%_22%)] bg-[linear-gradient(180deg,hsl(30_7%_17%),hsl(30_6%_10%))] text-[hsl(40_30%_96%)] shadow-[inset_0_1px_0_hsl(0_0%_100%/0.09)]',
}
const DARK_CARD =
  'dark:border-border/80 dark:bg-card dark:bg-none dark:text-card-foreground dark:shadow-none'

/** Secondary text, warmed to sit in the scene in light mode. */
const META = 'text-[hsl(30_9%_43%)] dark:text-muted-foreground'

function Card({
  children,
  className = '',
  tone = 'paper',
}: {
  children: ReactNode
  className?: string
  tone?: keyof typeof TONES
}) {
  return (
    <div
      className={`h-full w-full rounded-[14px] border ${TONES[tone]} ${DARK_CARD} ${className}`}
    >
      {children}
    </div>
  )
}

function CheckMark({ isDone }: { isDone: boolean }) {
  return (
    <span className="relative flex h-[18px] w-[18px] shrink-0 items-center justify-center">
      <m.span
        className="absolute inset-0 rounded-full border-[1.5px]"
        initial={false}
        animate={{
          backgroundColor: isDone
            ? 'hsl(var(--brand-green))'
            : 'hsl(var(--brand-green) / 0)',
          borderColor: isDone
            ? 'hsl(var(--brand-green))'
            : 'hsl(var(--border-strong))',
          scale: isDone ? [1, 1.18, 1] : 1,
        }}
        transition={{ duration: 0.35, ease: EASE }}
      />
      <svg viewBox="0 0 12 12" className="relative h-[10px] w-[10px]">
        <m.path
          d="M2.5 6.2 L5 8.6 L9.6 3.6"
          fill="none"
          stroke="white"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={false}
          animate={{ pathLength: isDone ? 1 : 0 }}
          transition={{ duration: 0.3, delay: isDone ? 0.1 : 0, ease: EASE }}
        />
      </svg>
    </span>
  )
}

function Tag({ label, hue }: { label: string; hue: string }) {
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{
        color: `hsl(var(${hue}))`,
        backgroundColor: `hsl(var(${hue}) / 0.12)`,
      }}
    >
      {label}
    </span>
  )
}

function taskFragment(
  id: string,
  title: string,
  tag: string,
  hue: string,
  slot: number,
  /** Which tick (1–3) completes it; 0 stays open. */
  doneAt: number,
  z: number,
  chaos: [Pose, Pose],
): Fragment {
  return {
    id,
    w: COL,
    h: 56,
    z,
    order: { x: L, y: 230 + slot * 72 },
    chaos,
    render: ({ checked }) => {
      const isDone = doneAt > 0 && checked >= doneAt
      return (
        <Card className="flex items-center gap-3 px-4">
          <CheckMark isDone={isDone} />
          <m.span
            className="min-w-0 flex-1 truncate text-[14px] font-medium"
            initial={false}
            animate={{ opacity: isDone ? 0.45 : 1 }}
            transition={{ duration: 0.3 }}
          >
            {title}
          </m.span>
          <Tag label={tag} hue={hue} />
        </Card>
      )
    },
  }
}

function greeting(now: Date) {
  const h = now.getHours()
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

function Ring({ value }: { value: number }) {
  return (
    <svg viewBox="0 0 44 44" className="h-11 w-11 -rotate-90">
      <circle
        cx={22}
        cy={22}
        r={18}
        fill="none"
        strokeWidth={4}
        className="stroke-[hsl(34_22%_88%)] dark:stroke-muted"
      />
      <m.circle
        cx={22}
        cy={22}
        r={18}
        fill="none"
        strokeWidth={4}
        strokeLinecap="round"
        stroke="hsl(var(--brand-green))"
        initial={false}
        animate={{ pathLength: value }}
        transition={{ duration: 0.6, ease: EASE }}
      />
    </svg>
  )
}

const AVATARS = [
  { initials: 'MC', hue: '--brand-orange' },
  { initials: 'JT', hue: '--brand-blue' },
  { initials: 'AR', hue: '--brand-green' },
]

export const FRAGMENTS: Fragment[] = [
  {
    id: 'today',
    w: 600,
    h: 84,
    z: 6,
    order: { x: L, y: 130 },
    chaos: [
      { x: 150, y: 52, r: -8, s: 0.95 },
      { x: -70, y: 420, r: 12, s: 0.95 },
    ],
    render: ({ checked }) => {
      const now = new Date()
      return (
        <Card className="flex items-center justify-between px-5">
          <div>
            <p className="font-heading text-[20px] font-semibold leading-tight">
              {greeting(now)}
            </p>
            <p className={`mt-0.5 text-[13px] ${META}`}>
              {now.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}{' '}
              · 4 tasks · 1 meeting
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[13px] font-semibold tabular-nums">
              {checked} of 4 done
            </span>
            <Ring value={checked / 4} />
          </div>
        </Card>
      )
    },
  },
  taskFragment(
    'task-launch',
    'Review the launch plan',
    'Launch',
    '--brand-blue',
    0,
    1,
    3,
    [
      { x: -40, y: 250, r: 14 },
      { x: 390, y: 84, r: -10 },
    ],
  ),
  taskFragment(
    'task-invoice',
    'Send the October invoice',
    'Finance',
    '--brand-green',
    1,
    2,
    8,
    [
      { x: 430, y: 168, r: -16 },
      { x: 10, y: 150, r: 18 },
    ],
  ),
  taskFragment(
    'task-kickoff',
    'Prep the client kickoff',
    'Clients',
    '--brand-orange',
    2,
    3,
    5,
    [
      { x: 70, y: 470, r: 9 },
      { x: 470, y: 476, r: -14 },
    ],
  ),
  taskFragment(
    'task-roadmap',
    'Update the roadmap',
    'Product',
    '--brand-red',
    3,
    0,
    2,
    [
      { x: 470, y: 560, r: 18 },
      { x: 170, y: 300, r: -6 },
    ],
  ),
  {
    id: 'event',
    w: COL,
    h: 128,
    z: 4,
    order: { x: R, y: 230 },
    chaos: [
      { x: 290, y: 330, r: -6 },
      { x: 430, y: 262, r: 10 },
    ],
    render: () => (
      <Card tone="blue" className="flex gap-3 p-4">
        <span className="w-1 shrink-0 rounded-full bg-[hsl(var(--brand-blue))]" />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[15px] font-semibold">Design review</p>
              <p className={`text-[13px] ${META}`}>2:30 – 3:00 PM</p>
            </div>
            <span className="rounded-full bg-[hsl(var(--brand-blue)/0.12)] px-2 py-0.5 text-[11px] font-semibold text-[hsl(var(--brand-blue))]">
              In 20 min
            </span>
          </div>
          <div className="mt-auto flex -space-x-1.5">
            {AVATARS.map(a => (
              <span
                key={a.initials}
                className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-[hsl(220_70%_98%)] text-[10px] font-bold dark:border-card"
                style={{
                  color: `hsl(var(${a.hue}))`,
                  backgroundColor: `hsl(var(${a.hue}) / 0.16)`,
                }}
              >
                {a.initials}
              </span>
            ))}
          </div>
        </div>
      </Card>
    ),
  },
  {
    id: 'invoice',
    w: COL,
    h: 128,
    z: 9,
    order: { x: R, y: 374 },
    chaos: [
      { x: -50, y: 600, r: -12 },
      { x: 240, y: 560, r: 6 },
    ],
    render: ({ isPaid }) => (
      <Card className="flex flex-col p-4">
        <div className="flex items-center justify-between">
          <p className={`text-[13px] ${META}`}>Invoice INV-1042</p>
          <AnimatePresence mode="popLayout" initial={false}>
            <m.span
              key={isPaid ? 'paid' : 'sent'}
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                isPaid
                  ? 'bg-[hsl(var(--brand-green)/0.14)] text-[hsl(var(--brand-green))]'
                  : 'bg-[hsl(34_24%_92%)] text-[hsl(30_9%_43%)] dark:bg-muted dark:text-muted-foreground'
              }`}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 520, damping: 22 }}
            >
              {isPaid ? 'Paid' : 'Sent'}
            </m.span>
          </AnimatePresence>
        </div>
        <p className="mt-1 text-[15px] font-semibold">Northwind Co.</p>
        <p className="font-heading mt-auto text-[24px] font-semibold tabular-nums">
          $4,800.00
        </p>
      </Card>
    ),
  },
  {
    id: 'message',
    w: COL,
    h: 100,
    z: 7,
    order: { x: L, y: 518 },
    chaos: [
      { x: 470, y: 392, r: 7 },
      { x: -60, y: 600, r: -9 },
    ],
    render: () => (
      <Card className="flex gap-3 p-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-orange)/0.16)] text-[12px] font-bold text-[hsl(var(--brand-orange))]">
          MC
        </span>
        <div className="min-w-0">
          <p className="text-[13px]">
            <span className="font-semibold">Maya Chen</span>{' '}
            <span className={META}>· 2m</span>
          </p>
          <p className="mt-0.5 text-[14px] leading-snug">
            The deck looks great. Ship it!
          </p>
        </div>
      </Card>
    ),
  },
  {
    id: 'sticky',
    w: 138,
    h: 100,
    z: 10,
    order: { x: R, y: 518, r: -2 },
    chaos: [
      { x: 580, y: 74, r: 22 },
      { x: 80, y: 54, r: -24 },
    ],
    render: () => (
      <div className="relative h-full w-full rounded-[6px] bg-[linear-gradient(165deg,hsl(49_100%_77%),hsl(45_100%_64%))] p-3 text-[hsl(30_40%_14%)] shadow-[inset_0_-12px_16px_-14px_hsl(35_80%_28%/0.45)]">
        <span className="absolute -top-2 left-1/2 h-4 w-12 -translate-x-1/2 rotate-[-3deg] rounded-[2px] bg-white/55" />
        <p className="mt-1 text-[14px] font-semibold leading-snug">
          Call the printer before 4
        </p>
      </div>
    ),
  },
  {
    id: 'timer',
    w: 138,
    h: 100,
    z: 1,
    order: { x: R + 154, y: 518 },
    chaos: [
      { x: 220, y: 206, r: -20 },
      { x: 566, y: 640, r: 16 },
    ],
    render: () => (
      <Card tone="iron" className="flex flex-col p-3">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold text-[hsl(var(--brand-green))]">
          <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-green))] shadow-[0_0_6px_hsl(var(--brand-green))]" />
          Tracking
        </p>
        <p className="font-heading mt-1 text-[22px] font-semibold tabular-nums">
          1:24:10
        </p>
        <p className="mt-auto truncate text-[12px] text-[hsl(40_20%_96%/0.55)] dark:text-muted-foreground">
          Launch plan
        </p>
      </Card>
    ),
  },
]
